import { useMutation, useQuery, useReactiveVar } from '@apollo/client'
import { useRef } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'

import {
  REMOVE_CUSTOM_COVER,
  UPLOAD_CUSTOM_COVER
} from 'data/layoutcreator/mutations/cover'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { customCoverRefreshCountVar } from 'data/store/cover'
import { CoverSides } from 'shared/types/global'
import { usePdfPreviewImage } from 'shared/hooks/usePdfPreviewImage'
import Button from 'shared/ui/Button_2'
import FileUpload from 'shared/ui/FileUpload'
import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import { Cover, CustomCoverSettingsWrapper, Section } from './Style'

type CustomCoverEditingPanelProps = {
  coverSide: CoverSides
}

const CustomCoverEditingPanel: React.FC<
  CustomCoverEditingPanelProps
> = ({ coverSide }) => {
  const { formatMessage } = useIntl()
  const { projectId } = useParams()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const customCoverRefreshCount = useReactiveVar(
    customCoverRefreshCountVar
  )

  const { data: checkForCustomCoverData } = useQuery(
    CHECK_FOR_CUSTOM_COVER,
    {
      variables: {
        projectId: projectId!
      }
    }
  )
  const coverPath =
    checkForCustomCoverData?.checkForCustomCover?.[coverSide] || ''
  const rawPreviewUrl = coverPath
    ? `${process.env.REACT_APP_REST_URL}${coverPath}${
        coverPath.includes('?') ? '&' : '?'
      }refreshCount=${customCoverRefreshCount}`
    : ''
  const { previewUrl, isLoading } = usePdfPreviewImage(
    rawPreviewUrl,
    {
      pageNumber: 1,
      scale: 1.5
    }
  )
  const showSkeleton =
    Boolean(rawPreviewUrl) && (isLoading || !previewUrl)

  const [uploadCustomCover, { loading: uploadLoading }] = useMutation(
    UPLOAD_CUSTOM_COVER,
    {
      refetchQueries: [
        {
          query: CHECK_FOR_CUSTOM_COVER,
          variables: {
            projectId: projectId!
          }
        }
      ],
      onCompleted: () => {
        customCoverRefreshCountVar(customCoverRefreshCount + 1)
      }
    }
  )

  const [removeCustomCover, { loading: removeLoading }] = useMutation(
    REMOVE_CUSTOM_COVER,
    {
      refetchQueries: [
        {
          query: CHECK_FOR_CUSTOM_COVER,
          variables: {
            projectId: projectId!
          }
        }
      ]
    }
  )

  const handleFileSelect = async (file: File) => {
    const toastId = toast.loading(
      formatMessage({
        id: 'Panel.coverStartEditing.uploadOwnCover.upload.loading'
      })
    )

    try {
      const { data } = await uploadCustomCover({
        variables: {
          projectId: projectId!,
          side: coverSide,
          file
        }
      })

      if (data?.uploadCustomCover) {
        toast.success(
          formatMessage({
            id: 'Panel.coverStartEditing.uploadOwnCover.upload.success'
          }),
          { id: toastId }
        )
      } else {
        toast.error(
          formatMessage({
            id: 'Panel.coverStartEditing.uploadOwnCover.upload.error'
          }),
          { id: toastId }
        )
      }
    } catch (error) {
      toast.error(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.upload.error'
        }),
        { id: toastId }
      )
    }
  }

  const handleRemoveCustomCover = async () => {
    const toastId = toast.loading(
      formatMessage({
        id: 'Panel.coverStartEditing.uploadOwnCover.remove.loading'
      })
    )

    try {
      const { data } = await removeCustomCover({
        variables: {
          projectId: projectId!,
          side: coverSide
        }
      })

      if (data?.removeCustomCover) {
        toast.success(
          formatMessage({
            id: 'Panel.coverStartEditing.uploadOwnCover.remove.success'
          }),
          { id: toastId }
        )
      } else {
        toast.error(
          formatMessage({
            id: 'Panel.coverStartEditing.uploadOwnCover.remove.error'
          }),
          { id: toastId }
        )
      }
    } catch (error) {
      toast.error(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.remove.error'
        }),
        { id: toastId }
      )
    }
  }

  return (
    <Flex direction="column" rowGap={32}>
      <CustomCoverSettingsWrapper>
        <Section>
          <Section.Heading>
            <Section.Title>
              {formatMessage({
                id: 'Panel.customCoverEditing.title'
              })}
            </Section.Title>
            <Section.Description>
              {formatMessage({
                id: 'Panel.customCoverEditing.description'
              })}
            </Section.Description>

            {showSkeleton ? (
              <Skeleton height={200} width="100%" radius={8} />
            ) : (
              <Cover src={previewUrl || ''} />
            )}
          </Section.Heading>
          <Section.Content>
            <Flex direction="column" rowGap={8}>
              <Button
                textId="Panel.coverStartEditing.uploadOwnCover.button.replace"
                intent="secondary-base"
                loading={uploadLoading}
                onPress={() => {
                  fileInputRef.current?.click()
                }}
              />
              <Button
                textId="Panel.coverStartEditing.uploadOwnCover.button.remove"
                intent="destructive"
                loading={removeLoading}
                onPress={handleRemoveCustomCover}
              />
            </Flex>
          </Section.Content>
        </Section>
      </CustomCoverSettingsWrapper>

      <FileUpload ref={fileInputRef} onSelect={handleFileSelect} />
    </Flex>
  )
}

export default CustomCoverEditingPanel
